<script lang="ts">
	import { onMount } from 'svelte';
	import EmbeddedChat from './Chat.svelte';
	import { appStore } from '../../../stores/appStore';

	const sidebarMinWidth = 300;
	const sidebarOptions: { [key: string]: string[] } = {
		font: [
			'button-color',
			'text-color',
			'avi-chat-text-color',
			'chat-input-text-color',
			'link-text-color',
			'send-icon-color'
		],
		secondaryFont: ['source-card-link-color'],
		background: [
			'button-bg',
			'button-bg-alternative',
			'source-card-bg-color',
			'retry-button-bg'
		],
		border: [
			'chat-input-border',
			'button-border-color',
			'link-background'
		],
		input: ['chat-input-bg']
	};

	let showingChat: boolean = false;
	let resizeBlockPos: number = 410;
	let sidebarWidth: number = 400;
	let innerWidth: number;
	let isDragging: boolean = false;
	let isShowingResizeBlock: boolean = true;
	let sidebarStyles: string = '';
	let resizeBarStyles: string = '';
	let buttonStyles: string = '';

	let resultSidebarOptions: { [key: string]: string } = {};
	$: {
		for (const optionGroup in sidebarOptions) {
			for (const optionName of sidebarOptions[optionGroup]) {
				resultSidebarOptions[optionName] = $appStore.chatWidgetParams.appearance[optionGroup];
			}
		}
	}
	$: isCustomizePage = !!$appStore.chatWidgetParams.isCustomizePage;
	$: sidebarPosition = isCustomizePage ? 'right' : $appStore.chatWidgetParams.appearance.sidebarPosition;

	let cssStyles: string;
	$: cssStyles = Object.entries(resultSidebarOptions)
		.map(([key, value]) => `--${key}:${value}`)
		.join(';');
	$: maxWidth = innerWidth - 30;

	const onDragStart = () => {
		isDragging = true;
		return { x: resizeBlockPos, y: 0 };
	};
	const onDragMove = (e: PointerEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isDragging) return;

		const newWidth = sidebarPosition === 'left' ? e.clientX - 12 : innerWidth - e.clientX - 12;
		sidebarWidth = Math.min(Math.max(sidebarMinWidth, newWidth), maxWidth);
	};
	const onDragEnd = () => {
		isDragging = false;
	};

	$: isShowingResizeBlock = innerWidth >= 500;

	$: {
		if (innerWidth < 500) {
			sidebarWidth = innerWidth;
			resizeBlockPos = innerWidth + 10;
		} else if (innerWidth - 10 < resizeBlockPos) {
			resizeBlockPos = innerWidth - 10;
			sidebarWidth = resizeBlockPos - 10;
		}
	}

	$: {
		document.body.style.overflowY = showingChat ? 'hidden' : 'unset';
	}

	$: {
		const result: {
			width: string;
			left: string;
			right: string;
			'border-left': string;
			'border-right': string;
		} = {
			width: '',
			left: '',
			right: '',
			'border-left': '',
			'border-right': ''
		};
		result.width = `${sidebarWidth}px`;
		result.right = sidebarPosition === 'right' ? `${showingChat ? 0 : -sidebarWidth - 20}px` : 'none';
		result.left = sidebarPosition === 'left' ? `${showingChat ? 0 : -sidebarWidth - 20}px` : 'none';
		result['border-left'] = sidebarPosition === 'right' ? '1px var(--button-border-color) solid' : 'none';
		result['border-right'] = sidebarPosition === 'left' ? '1px var(--button-border-color) solid' : 'none';
		sidebarStyles = Object.entries(result)
			.map(([key, value]) => `${key}:${value}`)
			.join(';');
		resizeBarStyles = sidebarPosition === 'right' ? `right: ${sidebarWidth + 10}px` : `left: ${sidebarWidth + 10}px`;
		buttonStyles = sidebarPosition === 'right' ? 'right: 30px' : 'left: 30px';
	}

	onMount(() => {
		addEventListener('pointerup', onDragEnd);
		addEventListener('pointermove', (e) => {
			onDragMove(e);
		});

		return () => {
			removeEventListener('pointerup', onDragEnd);
			removeEventListener('pointermove', (e) => {
				onDragMove(e);
			});
		};
	});
</script>

<svelte:window bind:innerWidth />

<div style={cssStyles}>
	<div
		class="main-wrapper"
		style={sidebarStyles}
	>
		<EmbeddedChat
			bind:showChat={showingChat}
		/>
		{#if isShowingResizeBlock}
			<div
				class:showingChat
				class="resize-bar"
				class:isDragging
				style={resizeBarStyles}
				on:pointerdown={onDragStart}
			/>
		{/if}
	</div>

	<button
		on:click={() => {
			showingChat = !showingChat;
		}}
		class="open-chat-button"
		class:showingChat
		style={buttonStyles}
	>
		<div class="image-wrapper">
			<img alt="logo" src="{import.meta.env.VITE_HOST}/chat-logo.png" />
		</div>
		<span>Chat</span>
	</button>
</div>

<style lang="scss">
	.main-wrapper {
		background: var(--button-bg);
		position: fixed;
		height: 100vh;
		height: 100svh;
		color: white;
		top: 0;
		transition: right 0.5s, left 0.5s;
		z-index: 9999;
	}

	.resize-bar {
		position: absolute;
		width: 6px;
		height: 64px;
		top: calc(50vh - 32px);
		right: -10px;
		border-radius: 5px;
		background: var(--button-border-color);
		cursor: col-resize;
		z-index: 9999;
		user-select: none;
		&.isDragging {
			transition: none;
		}
	}

	.open-chat-button {
		position: fixed;
		bottom: 30px;
		width: 170px;
		height: 65px;
		border-radius: 32.5px;
		border: 1px var(--button-border-color) solid;
		color: var(--button-color);
		background: var(--button-bg);
		display: flex;
		align-items: center;
		justify-content: space-between;
		outline: none;
		cursor: pointer;
		padding: 11px 25px 11px 11px;
		z-index: 300;
		transition: all 0.3s 0.3s;
		opacity: 1;

		&.showingChat {
			opacity: 0;
		}

		.image-wrapper {
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 50%;
			border: 1px var(--button-border-color) solid;
			background: var(--button-bg-alternative);
			width: 42px;
			height: 42px;
			padding: 6px;
			img {
				width: 100%;
				height: 100%;
			}
		}
	}

	*::selection {
		background: var(--highlight-bg);
		color: var(--highlight-text-color);
	}

	:global(a) {
		color: inherit;
		text-decoration: none;
		box-sizing: border-box;
	}

	button {
		touch-action: manipulation;
		background: none;
		border: none;
		cursor: pointer;
		outline: none;
		user-select: none;
		font-size: 16px;
	}

	*::-webkit-scrollbar-track {
		border-radius: 10px;
		background-color: transparent;
	}

	*::-webkit-scrollbar {
		width: 6px;
		background-color: transparent;
	}

	*::-webkit-scrollbar-thumb {
		border-radius: 10px;
		background-color: #7d7d7d72;
	}

	* {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
		font-family: Inter;
		/* general */
		//--text-color: white;
		--highlight-bg: rgba(255, 255, 255, 0.8); // don't have params
		--highlight-text-color: black; // don't have params

		/* chat */
		--avi-chat-bubble-bg: transparent;
		--avi-chat-bubble-border: transparent;
		//--avi-chat-text-color: #e8e8e6;
		//--chat-input-bg: rgba(21, 21, 22, 0.8);
		//--chat-input-border: #393939;
		//--chat-input-text-color: white;
		//--retry-button-bg: #504df3;
		//--source-card-bg-color: #212327;

		/* mobile menu */
		//--link-background: whitesmoke;
		//--link-text-color: black;

		/* embed chat */
		//--button-color: #8d9191;
		//--button-bg: #19191a;
		//--button-bg-alternative: #202122;
		//--button-border-color: #2f3031;
	}
</style>
